import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AutomationsEmployeesService,
  EmployeeWorkspaceType,
  EmployeeWorkspace,
} from 'src/api/generated';

type CreateParams = {
  employeeId: string;
  version: string | number;
  workspaceType: EmployeeWorkspaceType;
  workspaceId: string;
};

type DeleteParams = {
  employeeId: string;
  version: string | number;
  workspaceType: EmployeeWorkspaceType;
  workspaceId: string;
};

export const useCreateEmployeeWorkspaceMutation = (
  onSuccess?: (created: EmployeeWorkspace) => void,
  onError?: (error: Error) => void
) => {
  const queryClient = useQueryClient();

  const mutationFn = async (
    params: CreateParams
  ): Promise<EmployeeWorkspace> => {
    const { employeeId, version, workspaceType, workspaceId } = params;
    return await AutomationsEmployeesService.createWorkspace(
      employeeId,
      version,
      {
        workspaceType,
        workspaceId,
      }
    );
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
    onSuccess: async (created, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          'automations/employees/workspaces',
          variables.employeeId,
          variables.version,
        ],
      });
      onSuccess?.(created);
    },
    onError: (err: Error) => onError?.(err),
  });

  return {
    createWorkspace: mutateAsync,
    loading: isPending,
    error: (error as Error) || null,
  };
};

export const useDeleteEmployeeWorkspaceMutation = (
  onSuccess?: (deleted: EmployeeWorkspace) => void,
  onError?: (error: Error) => void
) => {
  const queryClient = useQueryClient();

  const mutationFn = async (
    params: DeleteParams
  ): Promise<EmployeeWorkspace> => {
    const { employeeId, version, workspaceType, workspaceId } = params;
    return await AutomationsEmployeesService.deleteWorkspace(
      employeeId,
      version,
      workspaceType,
      workspaceId
    );
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
    onSuccess: async (deleted, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          'automations/employees/workspaces',
          variables.employeeId,
          variables.version,
        ],
      });
      onSuccess?.(deleted);
    },
    onError: (err: Error) => onError?.(err),
  });

  return {
    deleteWorkspace: mutateAsync,
    loading: isPending,
    error: (error as Error) || null,
  };
};
